import { useCallback, useMemo, useState } from 'react';
import type { QuestionOption } from '@logistics/contracts';
import { browserStorage, readScope, toggleScope, writeScope } from './scope-store';
import type { ScopeOption } from './use-chat-data';

/**
 * The SEARCH IN selection: the person's last choice, narrowed to what is
 * available here, or every available source. Ticking a chip remembers it.
 */
export function useScope(
  userId: string,
  scopes: readonly ScopeOption[] | undefined,
): { scope: QuestionOption[]; available: QuestionOption[]; toggle: (o: QuestionOption) => void } {
  const available = useMemo(
    () => (scopes ?? []).filter((s) => s.available).map((s) => s.option),
    [scopes],
  );
  const [picked, setPicked] = useState<QuestionOption[] | null>(null);
  const scope = useMemo(
    () =>
      picked === null
        ? readScope(browserStorage(), userId, available)
        : picked.filter((o) => available.includes(o)),
    [available, picked, userId],
  );

  const toggle = useCallback(
    (option: QuestionOption) => {
      const next = toggleScope(scope, option);
      writeScope(browserStorage(), userId, next);
      setPicked(next);
    },
    [scope, userId],
  );

  return { scope, available, toggle };
}
